/**
 * Invertible commands over MdyStudioProject (plan section 7; ADR
 *: insert, move, delete,
 * duplicate, updateNode, add/remove field validator, updateBehavior,
 * add/remove form validator.
 *
 * An array's `item` template cannot be moved/deleted directly (the type
 * requires exactly one item; there is no valid "empty" intermediate state).
 * Replace it via `insertNode` targeting an `arrayItem` placement instead.
 */
import { buildIndexes, createId, isDuplicateKindAllowed, isValidatorCompatible } from "@modyra/studio-model";
import { validatePlacement, validateRename } from "./placement.js";
function findNode(root, id) {
    if (root.id === id)
        return root;
    if (root.node === "group") {
        for (const child of root.children) {
            const found = findNode(child, id);
            if (found)
                return found;
        }
    }
    else if (root.node === "array" || root.node === "record") {
        return findNode(root.item, id);
    }
    return null;
}
function slotOf(root, id) {
    if (root.node === "group") {
        for (let i = 0; i < root.children.length; i++) {
            const child = root.children[i];
            if (child.id === id)
                return { kind: "child", parent: root, index: i };
            const nested = slotOf(child, id);
            if (nested)
                return nested;
        }
    }
    else if (root.node === "array" || root.node === "record") {
        if (root.item.id === id)
            return { kind: "item", parent: root };
        return slotOf(root.item, id);
    }
    return null;
}
function insert(root, node, placement) {
    if (placement.kind === "inside") {
        const parent = findNode(root, placement.parentId);
        if (!parent || parent.node !== "group") {
            throw new Error(`insert: "inside" target "${placement.parentId}" is not a group node`);
        }
        const index = Math.max(0, Math.min(placement.index, parent.children.length));
        parent.children.splice(index, 0, node);
        return;
    }
    if (placement.kind === "before" || placement.kind === "after") {
        const slot = slotOf(root, placement.targetId);
        if (!slot || slot.kind !== "child") {
            throw new Error(`insert: "${placement.kind}" target "${placement.targetId}" has no group parent`);
        }
        slot.parent.children.splice(slot.index + (placement.kind === "after" ? 1 : 0), 0, node);
        return;
    }
    const array = findNode(root, placement.arrayId);
    if (!array || (array.node !== "array" && array.node !== "record")) {
        throw new Error(`insert: "arrayItem" target "${placement.arrayId}" is not a collection node`);
    }
    // A row may hold a collection of either kind, at any depth: a collection inside a row is addressed
    // by the pattern its declaration has, so a second positional level is as addressable as the first.
    array.item = node;
}
function placementAfterSibling(parent, index) {
    return index === 0
        ? { kind: "inside", parentId: parent.id, index: 0 }
        : { kind: "after", targetId: parent.children[index - 1].id };
}
function collectIds(node, out = []) {
    out.push(node.id);
    if (node.node === "group")
        node.children.forEach((c) => collectIds(c, out));
    else if (node.node === "array" || node.node === "record")
        collectIds(node.item, out);
    return out;
}
function cloneWithFreshIds(node) {
    const copy = structuredClone(node);
    const visit = (n) => {
        n.id = createId("nd");
        if (n.node === "field") {
            for (const validator of n.validators)
                validator.id = createId("val");
            if (n.serverValidator)
                n.serverValidator.id = createId("val");
        }
        else if (n.node === "array" || n.node === "record") {
            n.validators.forEach((v) => (v.id = createId("val")));
            visit(n.item);
        }
        else {
            n.children.forEach(visit);
        }
    };
    visit(copy);
    return copy;
}
/**
 * The chain of nodes from the schema root down to `id`, root first, or `null` if it is not there.
 *
 * The chain is what a command needs in order to rebuild only what it changed: everything off the
 * path is untouched and can be shared with the project it came from.
 */
function pathToNode(root, id) {
    if (root.id === id)
        return [root];
    if (root.node === "group") {
        for (const child of root.children) {
            const below = pathToNode(child, id);
            if (below)
                return [root, ...below];
        }
    }
    else if (root.node === "array" || root.node === "record") {
        const below = pathToNode(root.item, id);
        if (below)
            return [root, ...below];
    }
    return null;
}
/** A parent with one of its children swapped, sharing every other child with the original. */
function withChildReplaced(parent, previous, next) {
    if (parent.node === "group") {
        return { ...parent, children: parent.children.map((child) => (child === previous ? next : child)) };
    }
    return { ...parent, item: next };
}
/**
 * The project with one node changed, copying the path to it and sharing everything else.
 *
 * A command that begins `structuredClone(project)` pays for the whole document to change one string:
 * measured on a thousand fields, updating one label cost 96% of copying all thousand, so the price of
 * an edit was set by the size of the project rather than by the edit. Every node off the path is the
 * same object it was, which is what makes the cost proportional to what changed.
 *
 * The touched node itself is still deep-copied: `change` may push to an array or write a nested
 * member, and a shallow copy would reach through into the project this one is meant to leave alone.
 * That bounds the copy by the edited node's own subtree rather than by the document.
 *
 * `null` when the node is not there, so a caller reports it rather than writing into nothing.
 */
function withNode(project, nodeId, change) {
    const path = pathToNode(project.schema, nodeId);
    if (path === null)
        return null;
    let next = structuredClone(path[path.length - 1]);
    change(next);
    for (let depth = path.length - 2; depth >= 0; depth -= 1) {
        next = withChildReplaced(path[depth], path[depth + 1], next);
    }
    return { ...project, schema: next };
}
function error(code, message, nodeId) {
    return [{ code, severity: "error", message, ...(nodeId ? { nodeId } : {}) }];
}
/** Merges `patch` into `target`, treating an explicit `undefined` value as "delete this key". */
function applyPatch(target, patch) {
    for (const [key, value] of Object.entries(patch)) {
        if (value === undefined)
            delete target[key];
        else
            target[key] = value;
    }
}
/** Captures the pre-patch values of `patch`'s keys from `source`, `undefined` if the key wasn't present. */
function capturePatch(source, patch) {
    const original = {};
    for (const key of Object.keys(patch)) {
        original[key] = key in source ? source[key] : undefined;
    }
    return original;
}
export function inspectDelete(project, nodeId) {
    const idx = buildIndexes(project);
    const node = idx.nodeById.get(nodeId);
    const descendantIds = node ? collectIds(node).slice(1) : [];
    const referencedBy = [...(idx.referencesByTargetNode.get(nodeId) ?? [])];
    for (const id of descendantIds)
        referencedBy.push(...(idx.referencesByTargetNode.get(id) ?? []));
    return {
        requiresConfirmation: descendantIds.length > 0 || referencedBy.length > 0,
        descendantIds,
        referencedBy: [...new Set(referencedBy)],
    };
}
export function createInsertCommand(node, placement) {
    return {
        kind: "insert",
        description: `Insert ${node.name}`,
        affectedIds: collectIds(node),
        validate(project) {
            const idx = buildIndexes(project);
            if (idx.nodeById.has(node.id))
                return error("DUPLICATE_ID", `Node ID ${node.id} already exists`, node.id);
            return validatePlacement(project, idx, { nodeId: node.id, name: node.name, placement });
        },
        apply(project) {
            const copy = structuredClone(project);
            insert(copy.schema, structuredClone(node), placement);
            return copy;
        },
        inverse(before) {
            if (placement.kind === "arrayItem") {
                // Inserting as arrayItem *replaces* the array's item; the true inverse is restoring the old one.
                const array = findNode(before.schema, placement.arrayId);
                if (!array || (array.node !== "array" && array.node !== "record"))
                    throw new Error(`insert.inverse: array "${placement.arrayId}" missing`);
                return createInsertCommand(structuredClone(array.item), placement);
            }
            return createDeleteCommand(node.id, true);
        },
    };
}
export function createDeleteCommand(nodeId, confirmed = false) {
    return {
        kind: "delete",
        description: `Delete ${nodeId}`,
        affectedIds: [nodeId],
        validate(project) {
            if (nodeId === project.schema.id)
                return error("MALFORMED_COMMAND", "Cannot delete root", nodeId);
            const slot = slotOf(project.schema, nodeId);
            if (!slot)
                return error("MALFORMED_COMMAND", "Node does not exist", nodeId);
            if (slot.kind === "item") {
                return error("MALFORMED_COMMAND", "Replace the array item or delete its array", nodeId);
            }
            if (inspectDelete(project, nodeId).requiresConfirmation && !confirmed) {
                return error("CONFIRM_DELETE", "Node has children or references and requires confirmation", nodeId);
            }
            return [];
        },
        apply(project) {
            const copy = structuredClone(project);
            const slot = slotOf(copy.schema, nodeId);
            if (!slot || slot.kind !== "child")
                throw new Error(`delete: node "${nodeId}" is not deletable`);
            slot.parent.children.splice(slot.index, 1);
            return copy;
        },
        inverse(before) {
            const slot = slotOf(before.schema, nodeId);
            if (!slot || slot.kind !== "child")
                throw new Error(`delete.inverse: node "${nodeId}" missing`);
            const node = structuredClone(slot.parent.children[slot.index]);
            return createInsertCommand(node, placementAfterSibling(slot.parent, slot.index));
        },
    };
}
export function createMoveCommand(nodeId, placement) {
    return {
        kind: "move",
        description: `Move ${nodeId}`,
        affectedIds: [nodeId],
        validate(project) {
            if (nodeId === project.schema.id)
                return error("MALFORMED_COMMAND", "Cannot move root", nodeId);
            const slot = slotOf(project.schema, nodeId);
            if (!slot || slot.kind === "item")
                return error("MALFORMED_COMMAND", "Node is not movable", nodeId);
            const idx = buildIndexes(project);
            const node = idx.nodeById.get(nodeId);
            return validatePlacement(project, idx, { nodeId, name: node.name, placement });
        },
        apply(project) {
            const copy = structuredClone(project);
            const source = slotOf(copy.schema, nodeId);
            if (!source || source.kind !== "child")
                throw new Error(`move: node "${nodeId}" is not movable`);
            const [node] = source.parent.children.splice(source.index, 1);
            // Same-parent "inside" placement uses an absolute pre-removal index; shift it after the splice.
            let target = placement;
            if (placement.kind === "inside" && placement.parentId === source.parent.id && placement.index > source.index) {
                target = { ...placement, index: placement.index - 1 };
            }
            insert(copy.schema, node, target);
            return copy;
        },
        inverse(before) {
            const slot = slotOf(before.schema, nodeId);
            if (!slot || slot.kind !== "child")
                throw new Error(`move.inverse: node "${nodeId}" missing`);
            return createMoveCommand(nodeId, placementAfterSibling(slot.parent, slot.index));
        },
    };
}
export function createDuplicateCommand(nodeId, placement) {
    // Computed once (lazily, on first validate()/apply() call) and cached — NOT recomputed on
    // every validate() call, so repeated validation (e.g. a preview UI) doesn't silently swap
    // out which cloned node id apply()/inverse() end up agreeing on.
    let duplicate = null;
    const ensureDuplicate = (source) => {
        if (!duplicate) {
            duplicate = cloneWithFreshIds(source);
            duplicate.name = `${duplicate.name}Copy`;
        }
        return duplicate;
    };
    return {
        kind: "duplicate",
        description: `Duplicate ${nodeId}`,
        affectedIds: [nodeId],
        validate(project) {
            const slot = slotOf(project.schema, nodeId);
            if (!slot || slot.kind !== "child")
                return error("MALFORMED_COMMAND", "Node is not duplicable", nodeId);
            const candidate = ensureDuplicate(slot.parent.children[slot.index]);
            const target = placement ?? { kind: "inside", parentId: slot.parent.id, index: slot.index + 1 };
            return validatePlacement(project, buildIndexes(project), { nodeId: candidate.id, name: candidate.name, placement: target });
        },
        apply(project) {
            const slot = slotOf(project.schema, nodeId);
            if (!slot || slot.kind !== "child")
                throw new Error(`duplicate: node "${nodeId}" missing`);
            const candidate = ensureDuplicate(slot.parent.children[slot.index]);
            const target = placement ?? { kind: "inside", parentId: slot.parent.id, index: slot.index + 1 };
            const copy = structuredClone(project);
            insert(copy.schema, structuredClone(candidate), target);
            return copy;
        },
        inverse() {
            if (!duplicate)
                throw new Error("duplicate.inverse: called before validate()/apply()");
            return createDeleteCommand(duplicate.id, true);
        },
    };
}
export function createUpdateNodeCommand(nodeId, patch) {
    return {
        kind: "updateNode",
        description: `Update ${nodeId}`,
        affectedIds: [nodeId],
        validate(project) {
            const idx = buildIndexes(project);
            if (!idx.nodeById.has(nodeId))
                return error("MALFORMED_COMMAND", "Node does not exist", nodeId);
            return patch.name === undefined ? [] : validateRename(idx, nodeId, patch.name);
        },
        apply(project) {
            const next = withNode(project, nodeId, (node) => {
                applyPatch(node, patch);
            });
            if (next === null)
                throw new Error(`updateNode: node "${nodeId}" missing`);
            return next;
        },
        inverse(before) {
            const node = findNode(before.schema, nodeId);
            if (!node)
                throw new Error(`updateNode.inverse: node "${nodeId}" missing`);
            const original = capturePatch(node, patch);
            return createUpdateNodeCommand(nodeId, original);
        },
    };
}
export function createAddValidatorCommand(nodeId, validator) {
    return {
        kind: "addValidator",
        description: `Add ${validator.kind}`,
        affectedIds: [nodeId, validator.id],
        validate(project) {
            const node = buildIndexes(project).nodeById.get(nodeId);
            if (!node || node.node !== "field")
                return error("INVALID_VALIDATOR_TARGET", "Validator target must be a field", nodeId);
            if (node.validators.some((v) => v.id === validator.id))
                return error("DUPLICATE_ID", "Validator ID exists", nodeId);
            if (!isValidatorCompatible(validator.kind, node.valueType)) {
                return error("INCOMPATIBLE_VALIDATOR_TYPE", `Validator "${validator.kind}" does not support value type "${node.valueType}"`, nodeId);
            }
            if (!isDuplicateKindAllowed(validator.kind) && node.validators.some((v) => v.kind === validator.kind)) {
                return error("DUPLICATE_VALIDATOR_KIND", `Field already has a "${validator.kind}" validator`, nodeId);
            }
            return [];
        },
        apply(project) {
            let found = false;
            const next = withNode(project, nodeId, (node) => {
                if (node.node !== "field")
                    return;
                found = true;
                node.validators.push(structuredClone(validator));
            });
            if (next === null || !found)
                throw new Error(`addValidator: field "${nodeId}" missing`);
            return next;
        },
        inverse() {
            return createRemoveValidatorCommand(nodeId, validator.id);
        },
    };
}
export function createRemoveValidatorCommand(nodeId, validatorId) {
    return {
        kind: "removeValidator",
        description: `Remove ${validatorId}`,
        affectedIds: [nodeId, validatorId],
        validate(project) {
            const node = buildIndexes(project).nodeById.get(nodeId);
            if (!node || node.node !== "field" || !node.validators.some((v) => v.id === validatorId)) {
                return error("MALFORMED_COMMAND", "Validator does not exist", nodeId);
            }
            return [];
        },
        apply(project) {
            let found = false;
            const next = withNode(project, nodeId, (node) => {
                if (node.node !== "field")
                    return;
                found = true;
                node.validators.splice(node.validators.findIndex((v) => v.id === validatorId), 1);
            });
            if (next === null || !found)
                throw new Error(`removeValidator: field "${nodeId}" missing`);
            return next;
        },
        inverse(before) {
            const node = findNode(before.schema, nodeId);
            if (!node || node.node !== "field")
                throw new Error(`removeValidator.inverse: field "${nodeId}" missing`);
            const validator = node.validators.find((v) => v.id === validatorId);
            return createAddValidatorCommand(nodeId, structuredClone(validator));
        },
    };
}
/** Edits an existing validator's config (e.g. a pattern's regex) without changing its id or kind. */
export function createUpdateValidatorCommand(nodeId, validatorId, patch) {
    return {
        kind: "updateValidator",
        description: `Update validator ${validatorId}`,
        affectedIds: [nodeId, validatorId],
        validate(project) {
            const node = buildIndexes(project).nodeById.get(nodeId);
            if (!node || node.node !== "field" || !node.validators.some((v) => v.id === validatorId)) {
                return error("MALFORMED_COMMAND", "Validator does not exist", nodeId);
            }
            return [];
        },
        apply(project) {
            const copy = structuredClone(project);
            const node = findNode(copy.schema, nodeId);
            if (!node || node.node !== "field")
                throw new Error(`updateValidator: field "${nodeId}" missing`);
            const validator = node.validators.find((v) => v.id === validatorId);
            if (!validator)
                throw new Error(`updateValidator: validator "${validatorId}" missing`);
            applyPatch(validator, patch);
            return copy;
        },
        inverse(before) {
            const node = findNode(before.schema, nodeId);
            if (!node || node.node !== "field")
                throw new Error(`updateValidator.inverse: field "${nodeId}" missing`);
            const validator = node.validators.find((v) => v.id === validatorId);
            if (!validator)
                throw new Error(`updateValidator.inverse: validator "${validatorId}" missing`);
            const original = capturePatch(validator, patch);
            return createUpdateValidatorCommand(nodeId, validatorId, original);
        },
    };
}
/** Replaces a select/multiselect field's option list wholesale ("properties options"). */
export function createSetFieldOptionsCommand(nodeId, options) {
    return {
        kind: "setFieldOptions",
        description: `Update options for ${nodeId}`,
        affectedIds: [nodeId],
        validate(project) {
            const node = buildIndexes(project).nodeById.get(nodeId);
            if (!node || node.node !== "field")
                return error("INVALID_VALIDATOR_TARGET", "Options target must be a field", nodeId);
            const values = options.map((o) => o.value);
            if (new Set(values).size !== values.length) {
                return error("DUPLICATE_OPTION_VALUE", "Option values must be unique", nodeId);
            }
            return [];
        },
        apply(project) {
            let found = false;
            const next = withNode(project, nodeId, (node) => {
                if (node.node !== "field")
                    return;
                found = true;
                node.options = structuredClone(options);
            });
            if (next === null || !found)
                throw new Error(`setFieldOptions: field "${nodeId}" missing`);
            return next;
        },
        inverse(before) {
            const node = findNode(before.schema, nodeId);
            if (!node || node.node !== "field")
                throw new Error(`setFieldOptions.inverse: field "${nodeId}" missing`);
            return createSetFieldOptionsCommand(nodeId, structuredClone(node.options ?? []));
        },
    };
}
export function createUpdateBehaviorCommand(patch) {
    return {
        kind: "updateBehavior",
        description: "Update form behavior",
        affectedIds: [],
        validate() {
            return [];
        },
        apply(project) {
            const copy = structuredClone(project);
            applyPatch(copy.behaviors, structuredClone(patch));
            return copy;
        },
        inverse(before) {
            const original = capturePatch(before.behaviors, patch);
            return createUpdateBehaviorCommand(original);
        },
    };
}
/** Renames the project itself. The form name is user-facing metadata, not a node, so it needs its own invertible command. */
export function createRenameProjectCommand(name) {
    return {
        kind: "renameProject",
        description: `Rename form to ${name}`,
        affectedIds: [],
        validate() {
            return name.trim() ? [] : error("EMPTY_NAME", "Form name cannot be empty");
        },
        apply(project) {
            // The spread is the whole copy this command needs. It changes one top-level string and
            // touches nothing under `schema`, so the deep clone that used to sit here duplicated every
            // node in the document to leave all of them identical — the cost of the project paid for the
            // cost of the edit. Every other command in this file mutates its copy in place and still
            // needs one; this one never did.
            return { ...project, name: name.trim() };
        },
        inverse(before) {
            return createRenameProjectCommand(before.name);
        },
    };
}
/**
 * Replaces the whole form layout. Arrangement is a tree, and every authoring gesture
 * (wrap in a section, split into columns, pull a field out) is a transform of that tree —
 * so one invertible command over the whole tree beats four commands that each have to
 * re-derive the same structure. `description` is what the UI shows and undo announces.
 */
export function createUpdateLayoutCommand(layout, description = "Update layout") {
    return {
        kind: "updateLayout",
        description,
        affectedIds: [],
        validate() {
            return [];
        },
        apply(project) {
            const copy = structuredClone(project);
            copy.presentation = { ...copy.presentation, layout: structuredClone(layout) };
            return copy;
        },
        inverse(before) {
            return createUpdateLayoutCommand(structuredClone(before.presentation.layout ?? []), description);
        },
    };
}
export function createAddFormValidatorCommand(validator) {
    return {
        kind: "addFormValidator",
        description: `Add form validator ${validator.id}`,
        affectedIds: [validator.id],
        validate(project) {
            return project.formValidators.some((v) => v.id === validator.id) ? error("DUPLICATE_ID", "Validator ID exists") : [];
        },
        apply(project) {
            const copy = structuredClone(project);
            copy.formValidators.push(structuredClone(validator));
            return copy;
        },
        inverse() {
            return createRemoveFormValidatorCommand(validator.id);
        },
    };
}
export function createRemoveFormValidatorCommand(validatorId) {
    return {
        kind: "removeFormValidator",
        description: `Remove ${validatorId}`,
        affectedIds: [validatorId],
        validate(project) {
            return project.formValidators.some((v) => v.id === validatorId)
                ? []
                : error("MALFORMED_COMMAND", "Form validator does not exist");
        },
        apply(project) {
            const copy = structuredClone(project);
            copy.formValidators = copy.formValidators.filter((v) => v.id !== validatorId);
            return copy;
        },
        inverse(before) {
            const validator = before.formValidators.find((v) => v.id === validatorId);
            return createAddFormValidatorCommand(structuredClone(validator));
        },
    };
}
/** Edits an existing form/cross-field validator's dependencies/condition/message/errorTarget in place. */
export function createUpdateFormValidatorCommand(validatorId, patch) {
    return {
        kind: "updateFormValidator",
        description: `Update form validator ${validatorId}`,
        affectedIds: [validatorId],
        validate(project) {
            return project.formValidators.some((v) => v.id === validatorId)
                ? []
                : error("MALFORMED_COMMAND", "Form validator does not exist");
        },
        apply(project) {
            const copy = structuredClone(project);
            const validator = copy.formValidators.find((v) => v.id === validatorId);
            if (!validator)
                throw new Error(`updateFormValidator: "${validatorId}" missing`);
            applyPatch(validator, patch);
            return copy;
        },
        inverse(before) {
            const validator = before.formValidators.find((v) => v.id === validatorId);
            if (!validator)
                throw new Error(`updateFormValidator.inverse: "${validatorId}" missing`);
            const original = capturePatch(validator, patch);
            return createUpdateFormValidatorCommand(validatorId, original);
        },
    };
}
/** Adds, replaces, or (passing `null`) removes a field's server validator wholesale. */
export function createSetServerValidatorCommand(nodeId, serverValidator) {
    return {
        kind: "setServerValidator",
        description: serverValidator ? `Set server validator on ${nodeId}` : `Remove server validator from ${nodeId}`,
        affectedIds: [nodeId],
        validate(project) {
            const node = buildIndexes(project).nodeById.get(nodeId);
            return !node || node.node !== "field" ? error("INVALID_VALIDATOR_TARGET", "Server validator target must be a field", nodeId) : [];
        },
        apply(project) {
            let found = false;
            const next = withNode(project, nodeId, (node) => {
                if (node.node !== "field")
                    return;
                found = true;
                if (serverValidator)
                    node.serverValidator = structuredClone(serverValidator);
                else
                    delete node.serverValidator;
            });
            if (next === null || !found)
                throw new Error(`setServerValidator: field "${nodeId}" missing`);
            return next;
        },
        inverse(before) {
            const node = findNode(before.schema, nodeId);
            if (!node || node.node !== "field")
                throw new Error(`setServerValidator.inverse: field "${nodeId}" missing`);
            return createSetServerValidatorCommand(nodeId, node.serverValidator ? structuredClone(node.serverValidator) : null);
        },
    };
}
/** Registers a symbolic implementation stub (: symbolic reference + generated stub, never inline code). */
export function createAddImplementationCommand(ref) {
    return {
        kind: "addImplementation",
        description: `Add implementation ${ref.displayName}`,
        affectedIds: [ref.id],
        validate(project) {
            return project.implementations[ref.id] ? error("DUPLICATE_ID", "Implementation ID exists") : [];
        },
        apply(project) {
            const copy = structuredClone(project);
            copy.implementations[ref.id] = structuredClone(ref);
            return copy;
        },
        inverse() {
            return createRemoveImplementationCommand(ref.id);
        },
    };
}
function createRemoveImplementationCommand(implementationId) {
    return {
        kind: "removeImplementation",
        description: `Remove implementation ${implementationId}`,
        affectedIds: [implementationId],
        validate() {
            return [];
        },
        apply(project) {
            const copy = structuredClone(project);
            delete copy.implementations[implementationId];
            return copy;
        },
        inverse(before) {
            const ref = before.implementations[implementationId];
            return createAddImplementationCommand(structuredClone(ref));
        },
    };
}
/**
 * Several commands as one, so a single action that has to touch both the schema and the
 * presentation is a single entry in the history.
 *
 * Dropping a control beside another is exactly that: the node is inserted or moved, *and* the two
 * are put into a column row. As two commands it took two undos to get back — the field would jump
 * out of the row and stay where it had been dropped, which is neither of the states the user was
 * in.
 *
 * Each step sees the project the step before it produced: `validate` runs the chain forward so a
 * later step is checked against the state it will actually meet, and `inverse` replays the chain to
 * hand each step the project it saw, then reverses the order — undoing a sequence is undoing its
 * parts backwards.
 */
export function createSequenceCommand(steps, description) {
    return {
        kind: "sequence",
        description,
        affectedIds: [...new Set(steps.flatMap((step) => step.affectedIds))],
        /**
         * Every step's findings, not the first step's.
         *
         * Returning at the first step that says *anything* is what a reader expects to mean "stop at the
         * first problem", and it does not: `CommandHistory` rejects on an **error**, so a step producing
         * an advisory made the sequence stop looking — and an invalid step after it was applied.
         *
         * Latent today, because every diagnostic in this file goes through one helper that hardcodes
         * `severity: "error"`. That is exactly why it is worth being right about now: the day a warning
         * is added, sequences stop being validated, and nothing about that change looks like it touches
         * sequences.
         *
         * An error still stops the walk — a step that must not apply cannot be threaded through to give
         * the next one a project to look at — while an advisory is collected and the walk goes on.
         */
        validate(project) {
            const found = [];
            let current = project;
            for (const step of steps) {
                const diagnostics = step.validate(current);
                found.push(...diagnostics);
                if (diagnostics.some((d) => d.severity === "error"))
                    return found;
                current = step.apply(current);
            }
            return found;
        },
        apply(project) {
            return steps.reduce((current, step) => step.apply(current), project);
        },
        inverse(before) {
            const seen = [before];
            for (const step of steps)
                seen.push(step.apply(seen[seen.length - 1]));
            const undone = steps.map((step, index) => step.inverse(seen[index])).reverse();
            return createSequenceCommand(undone, `Undo ${description}`);
        },
    };
}
